/**
 * VÉRAULT Member Suite — TravelDocumentsShelf (Wave 27.1 · Phase 4e).
 * ────────────────────────────────────────────────────────────────────
 * Uploaded files · insurance · visas · confirmations · custom docs.
 * Plus traveler intake forms (form_packets).
 *
 *   Calm, organized, minimal. NEVER feels like Dropbox.
 *   Upload requests get a quiet "we need you to share this" affordance.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Shield, ScrollText, Upload, Download, Clipboard } from "lucide-react";
import UploadDocumentModal from "./UploadDocumentModal";
import FormResponseModal from "./FormResponseModal";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

const CATEGORY_ICONS = {
  insurance: Shield,
  visa: ScrollText,
  customs: ScrollText,
  upload_request: Upload,
  passport: ScrollText,
  default: FileText,
};

function DocCard({ doc, isForm = false, onUploadClick, onFormClick }) {
  const cat = (doc.category || "").toLowerCase();
  const Icon = CATEGORY_ICONS[cat] || (isForm ? Clipboard : CATEGORY_ICONS.default);
  const isUploadRequest = cat === "upload_request";
  const isPendingForm = isForm && (doc.status || "").toLowerCase() !== "complete" && (doc.status || "").toLowerCase() !== "submitted";
  const dateLabel = doc.uploaded_at || doc.requested_at || doc.sent_at || doc.updated_at || doc.created_at;

  return (
    <article
      className={`rounded-md p-4 sm:p-5 border ${isUploadRequest || isPendingForm
        ? "bg-[hsl(var(--accent-gold)/0.04)] border-[hsl(var(--accent-gold)/0.32)]"
        : "bg-white/65 backdrop-blur-[2px] border-[rgba(0,0,0,0.06)]"}`}
      data-testid={isForm ? `form-card-${doc.id}` : `document-card-${doc.id}`}
    >
      <div className="flex items-start gap-3.5">
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--background-paper))] flex-shrink-0">
          <Icon className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.4} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            {isForm ? "Form" : (cat || "Document").replace("_", " ")}
          </p>
          <h3 className="mt-1 font-serif text-[16px] sm:text-[17px] leading-tight tracking-tight">
            {doc.title || doc.label}
          </h3>
          {dateLabel && (
            <p className="mt-1 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              {isUploadRequest ? "requested " : isPendingForm ? "sent " : "added "}
              {fmtDate(dateLabel)}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {isUploadRequest && (
              <button
                type="button"
                onClick={() => onUploadClick(doc)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--accent-gold))] transition-colors"
                data-testid={`upload-cta-${doc.id}`}
              >
                Upload file
                <Upload className="size-3" strokeWidth={1.5} />
              </button>
            )}
            {isPendingForm && (
              <button
                type="button"
                onClick={() => onFormClick(doc)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--accent-gold))] transition-colors"
                data-testid={`form-open-${doc.id}`}
              >
                Open form
                <Clipboard className="size-3" strokeWidth={1.5} />
              </button>
            )}
            {doc.file_url && !isUploadRequest && (
              <a
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] border border-[hsl(var(--border))] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] transition-colors"
                data-testid={`document-download-${doc.id}`}
              >
                View
                <Download className="size-3" strokeWidth={1.5} />
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function TravelDocumentsShelf({ documents = [], forms = [], onChanged }) {
  const [uploading, setUploading] = useState(null);
  const [formPacket, setFormPacket] = useState(null);
  const total = documents.length + forms.length;
  if (total === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      id="travel-documents"
      data-testid="essentials-travel-documents"
      className="scroll-mt-24"
    >
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Travel documents
        </p>
        <h2 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          Papers &amp; uploads.
        </h2>
      </header>
      <div className="space-y-3 sm:space-y-4">
        {documents.map((d) => (
          <DocCard
            key={d.id}
            doc={d}
            onUploadClick={setUploading}
            onFormClick={() => {}}
          />
        ))}
        {forms.map((f) => (
          <DocCard
            key={f.id}
            doc={f}
            isForm
            onUploadClick={() => {}}
            onFormClick={setFormPacket}
          />
        ))}
      </div>
      {uploading && (
        <UploadDocumentModal
          doc={uploading}
          onClose={() => setUploading(null)}
          onUploaded={() => onChanged?.()}
        />
      )}
      {formPacket && (
        <FormResponseModal
          packet={formPacket}
          onClose={() => setFormPacket(null)}
          onSubmitted={() => onChanged?.()}
        />
      )}
    </motion.section>
  );
}
